var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../player/DataContext';
import { saveState } from '../helper/stateManagerHelper';
import { bringManipulativesToFront } from '../helper/helper';
import globalStore from '../store';
// Padding outset on each side so the bounding box doesn't hug the tiles pixel-tight.
var PADDING = 12;
var ARROW_BASE = 10;
var ARROW_SHIFT = 50;
// Fraction tile `center` is always the top-left of the tile's bounding box
// (rectangles: the rect itself; circles: the full 300x300 circle) — unlike
// NMP's geometric-center convention — so bounds are a plain top-left + size sum.
var computeBounds = function (members) {
    var minX = Infinity;
    var minY = Infinity;
    var maxX = -Infinity;
    var maxY = -Infinity;
    members.forEach(function (m) {
        var _a, _b, _c, _d, _e;
        var d = m.data;
        var scale = (_a = d.scale) !== null && _a !== void 0 ? _a : 1;
        var w = (d.shapeType === 'circle' ? 300 : ((_c = (_b = d.dimensions) === null || _b === void 0 ? void 0 : _b.width) !== null && _c !== void 0 ? _c : 0)) * scale;
        var h = (d.shapeType === 'circle' ? 300 : ((_e = (_d = d.dimensions) === null || _d === void 0 ? void 0 : _d.height) !== null && _e !== void 0 ? _e : 0)) * scale;
        var left = d.center.x;
        var top = d.center.y;
        if (left < minX)
            minX = left;
        if (left + w > maxX)
            maxX = left + w;
        if (top < minY)
            minY = top;
        if (top + h > maxY)
            maxY = top + h;
    });
    if (!Number.isFinite(minX))
        return null;
    return {
        x: minX - PADDING,
        y: minY - PADDING,
        width: maxX - minX + PADDING * 2,
        height: maxY - minY + PADDING * 2,
    };
};
var FractionTilesSelectionGroup = function (_a) {
    var groupId = _a.groupId, members = _a.members, groupIndex = _a.groupIndex, groupCount = _a.groupCount;
    var t = useTranslation().t;
    var _b = useContext(DataContext), selectedTool = _b.selectedTool, setSelectedTool = _b.setSelectedTool, manipulative = _b.manipulative, setManipulative = _b.setManipulative, setContextMenu = _b.setContextMenu;
    var zoomLevel = globalStore.useStoreState(function (s) { return s.playerStore; }).zoomLevel;
    var isGroupActive = selectedTool.includes(groupId);
    var containerRef = useRef(null);
    var memberIds = useMemo(function () { return members.map(function (m) { return m.data.id; }); }, [members]);
    var bounds = useMemo(function () { return computeBounds(members); }, [members]);
    // Refs let the keyboard / drag handlers read the freshest member list
    // without re-binding listeners every commit.
    var membersRef = useRef(members);
    membersRef.current = members;
    var manipulativeRef = useRef(manipulative);
    manipulativeRef.current = manipulative;
    // -- Group drag --------------------------------------------------------
    var dragRef = useRef(null);
    var beginDrag = function (e) {
        if (!isGroupActive)
            return;
        var isTouch = 'touches' in e;
        var clientX = isTouch ? e.touches[0].clientX : e.clientX;
        var clientY = isTouch ? e.touches[0].clientY : e.clientY;
        e.stopPropagation();
        var snapshot = {};
        membersRef.current.forEach(function (m) {
            var d = m.data;
            snapshot[m.data.id] = { x: d.center.x, y: d.center.y };
        });
        dragRef.current = { startX: clientX, startY: clientY, snapshot: snapshot };
        var moved = false;
        var onMove = function (ev) {
            if (!dragRef.current)
                return;
            var cx = 'touches' in ev ? ev.touches[0].clientX : ev.clientX;
            var cy = 'touches' in ev ? ev.touches[0].clientY : ev.clientY;
            var dx = (cx - dragRef.current.startX) / zoomLevel;
            var dy = (cy - dragRef.current.startY) / zoomLevel;
            if (!moved && Math.abs(dx) + Math.abs(dy) < 3)
                return;
            moved = true;
            var snap = dragRef.current.snapshot;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    var sid = item.data.id;
                    if (!(sid in snap))
                        return item;
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: snap[sid].x + dx, y: snap[sid].y + dy } }) });
                });
            });
        };
        var onUp = function () {
            var _a, _b;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
            if (moved) {
                // Single batched undo entry for the whole group's move.
                var newCenters_1 = {};
                membersRef.current.forEach(function (m) {
                    var d = m.data;
                    newCenters_1[m.data.id] = { x: d.center.x, y: d.center.y };
                });
                var oldSnap = (_b = (_a = dragRef.current) === null || _a === void 0 ? void 0 : _a.snapshot) !== null && _b !== void 0 ? _b : {};
                var batchEntries = [
                    {
                        type: groupId,
                        id: groupId.split('_')[1],
                        propertyName: 'groupCenters',
                        old: JSON.stringify(oldSnap),
                        new: JSON.stringify(newCenters_1),
                    },
                ];
                // Bring the whole group to front on drop, mirroring individual fraction
                // tiles (see FractionTileManipulative.tsx). Once a group is active its
                // overlay captures all drag input here, so this is the only bring-to-front
                // path for a group being re-dragged after the initial click that selected it.
                var memberIdSet_1 = new Set(memberIds);
                var beforeIds = manipulativeRef.current.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                var orderedMemberIds_1 = manipulativeRef.current
                    .filter(function (m) { return memberIdSet_1.has(m.data.id); })
                    .map(function (m) { return m.data.id; });
                var alreadyAtFront = orderedMemberIds_1.length > 0 &&
                    beforeIds.slice(-orderedMemberIds_1.length).join(',') === orderedMemberIds_1.join(',');
                if (orderedMemberIds_1.length > 0 && !alreadyAtFront) {
                    var afterIds = __spreadArray(__spreadArray([], beforeIds.filter(function (bid) { return !memberIdSet_1.has(bid); }), true), orderedMemberIds_1, true);
                    setManipulative(function (prev) { return bringManipulativesToFront(prev, orderedMemberIds_1); });
                    batchEntries.push({
                        type: groupId,
                        id: groupId.split('_')[1],
                        propertyName: 'reorder',
                        old: JSON.stringify(beforeIds),
                        new: JSON.stringify(afterIds),
                    });
                }
                saveState(batchEntries);
            }
            dragRef.current = null;
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('touchend', onUp);
    };
    // -- Arrow-key move ----------------------------------------------------
    var arrowMoveBatchRef = useRef(null);
    var flushArrowMove = function () {
        var batch = arrowMoveBatchRef.current;
        if (!batch)
            return;
        var newCenters = {};
        membersRef.current.forEach(function (m) {
            var d = m.data;
            newCenters[m.data.id] = { x: d.center.x, y: d.center.y };
        });
        saveState({
            type: groupId,
            id: groupId.split('_')[1],
            propertyName: 'groupCenters',
            old: JSON.stringify(batch.initial),
            new: JSON.stringify(newCenters),
        });
        arrowMoveBatchRef.current = null;
    };
    var moveByArrow = function (dx, dy) {
        if (!arrowMoveBatchRef.current) {
            var initial_1 = {};
            membersRef.current.forEach(function (m) {
                var d = m.data;
                initial_1[m.data.id] = { x: d.center.x, y: d.center.y };
            });
            arrowMoveBatchRef.current = { initial: initial_1, timer: null };
        }
        if (arrowMoveBatchRef.current.timer) {
            clearTimeout(arrowMoveBatchRef.current.timer);
        }
        arrowMoveBatchRef.current.timer = setTimeout(flushArrowMove, 500);
        var memberIdSet = new Set(memberIds);
        setManipulative(function (prev) {
            return prev.map(function (item) {
                var sid = item.data.id;
                if (!memberIdSet.has(sid))
                    return item;
                var c = item.data.center;
                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: c.x + dx, y: c.y + dy } }) });
            });
        });
    };
    var onKeyDown = function (e) {
        var _a;
        if (e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ContextMenu' ||
            (e.shiftKey && e.key === 'F10')) {
            e.preventDefault();
            e.stopPropagation();
            var rect = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (rect) {
                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: groupId });
            }
            return;
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            flushArrowMove();
            e.currentTarget.blur();
            return;
        }
        var step = e.shiftKey ? ARROW_SHIFT : ARROW_BASE;
        if (e.key === 'ArrowRight') {
            e.preventDefault();
            moveByArrow(step, 0);
        }
        else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            moveByArrow(-step, 0);
        }
        else if (e.key === 'ArrowDown') {
            e.preventDefault();
            moveByArrow(0, step);
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            moveByArrow(0, -step);
        }
    };
    // Scale is intentionally NOT handled here: FractionTileController.tsx's own
    // scalePlus/scaleMinus handling already scales every tile matching the
    // selected shapeType and repositions adjacent chains — same convention as
    // NumberAndMoneyPieceSelectionGroup, which leaves NMP group scale to
    // GameController.tsx for the same reason.
    // -- onFocus: promote the group into selectedTool ----------------------
    var onFocus = function () {
        if (!selectedTool.includes(groupId))
            setSelectedTool([groupId]);
    };
    // -- onMouseDown: select then drag -------------------------------------
    var onMouseDown = function (e) {
        if (!selectedTool.includes(groupId))
            setSelectedTool([groupId]);
        beginDrag(e);
    };
    if (!bounds)
        return null;
    var ariaLabel = groupCount > 1
        ? t('text.fractionTileGroupAriaLabelIndexed', {
            index: groupIndex,
            groupCount: groupCount,
            count: members.length,
            defaultValue: "Fraction tile group ".concat(groupIndex, " of ").concat(groupCount, ", ").concat(members.length, " tiles"),
        })
        : t('text.fractionTileGroupAriaLabel', {
            count: members.length,
            defaultValue: "Group of ".concat(members.length, " fraction tiles"),
        });
    var wrapperStyle = {
        position: 'absolute',
        left: bounds.x,
        top: bounds.y,
        width: bounds.width,
        height: bounds.height,
        // Always non-interactive to pointer events — individual grouped tiles
        // (FractionTileManipulative.tsx) already own their own click/drag/select
        // via dragKey, so this overlay only needs to be a keyboard Tab-stop
        // (tabIndex/onKeyDown below, unaffected by pointerEvents). Leaving this
        // 'auto' when active let it win right-click hit-tests over the padding
        // gaps inside the group's bounding box (and, for circle tiles, their
        // transparent bounding-box corners), which resolved the context menu to
        // the group instead of the tile and hid Combine/Split.
        pointerEvents: 'none',
        cursor: isGroupActive ? 'grab' : 'default',
    };
    return (_jsx("div", { ref: containerRef, className: "fractionTilesSelectionGroup".concat(isGroupActive ? ' active' : ''), "data-manipulative-id": groupId, "data-group-active": isGroupActive ? 'true' : 'false', tabIndex: 0, role: "region", "aria-label": ariaLabel, style: wrapperStyle, onFocus: onFocus, onKeyDown: onKeyDown, onMouseDown: onMouseDown, onTouchStart: onMouseDown }));
};
export default FractionTilesSelectionGroup;
